import { Globe, Mail, MapPin, Phone } from 'lucide-react';
import type { Location } from '@court-sync/shared';
import type { PublicOrgOrganization } from '@/services/publicOrg';

interface PublicPlayEventsContactProps {
  organization: PublicOrgOrganization;
  locations?: Location[];
}

function resolveContactAddress(
  organization: PublicOrgOrganization,
  locations: Location[] = []
): string | null {
  return (
    organization.address ||
    locations[0]?.address ||
    locations[0]?.googleMapsAddress ||
    organization.googleMapsAddress ||
    null
  );
}

/** Contact block styled to match `OrgSidebar` on the public club home page. */
export function PublicPlayEventsContact({
  organization,
  locations = [],
}: PublicPlayEventsContactProps) {
  const address = resolveContactAddress(organization, locations);
  const hasContact = Boolean(
    address || organization.phone || organization.email || organization.website
  );

  if (!hasContact) return null;

  return (
    <div>
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        Contact
      </h3>
      <ul className="space-y-2 text-sm">
        {address ? (
          <li className="flex items-start gap-2">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <span>{address}</span>
          </li>
        ) : null}
        {organization.phone ? (
          <li className="flex items-center gap-2">
            <Phone className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <a href={`tel:${organization.phone}`} className="hover:underline">
              {organization.phone}
            </a>
          </li>
        ) : null}
        {organization.email ? (
          <li className="flex items-center gap-2">
            <Mail className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <a href={`mailto:${organization.email}`} className="hover:underline">
              {organization.email}
            </a>
          </li>
        ) : null}
        {organization.website ? (
          <li className="flex items-center gap-2">
            <Globe className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <a
              href={organization.website}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
              Website
            </a>
          </li>
        ) : null}
      </ul>
    </div>
  );
}
